import { apiClient } from "./api";

// Departments, the teams and roles beneath them, and who is placed on those.
//
// There is ONE department table: migration 017 folded the hierarchy into the
// shared `departments` table that ticketing already reads, with `type`
// separating a department from a team from a role and `parent_id` linking them.
// So a "department" here is the same record `masters/DepartmentMaster.js` edits,
// and creating one from the ATS makes it visible in the ticket screens too.
//
// The endpoints keep their /departments and /dept-nodes shapes: the split
// between "a department" and "a row inside one" is still real, it is just
// expressed by type rather than by two tables.
// These are `departments.type` values. Migration 017 folded the hierarchy into
// that shared table, so 'dept' is a department and these four are everything
// beneath one. 'project' is absent on purpose: those rows belong to ticketing.
export const NODE_TYPES = [
  { value: "div", label: "Division" },
  { value: "team", label: "Team" },
  { value: "role", label: "Role" },
  { value: "grade", label: "Grade" },
];

export const MEMBERSHIP_ROLES = [
  { value: "LEAD", label: "Lead" },
  { value: "HIRING_MANAGER", label: "Hiring manager" },
  { value: "RECRUITER", label: "Recruiter" },
  { value: "INTERVIEWER", label: "Interviewer" },
  { value: "MEMBER", label: "Member" },
];

// Deepest level the builder will create, counting the department itself as 1 —
// so a department plus five rungs of teams and roles. Must match MAX_DEPTH in
// the backend's departmentController.
export const MAX_DEPTH = 6;

// The hiring API answers a failure with HTTP 200 and { success: false,
// statusMessage } — only an unrouted path or a rejected token is a non-200. Read
// naively, `res.data.data` is then undefined and every screen renders as empty
// rather than as broken, which is the worst of the three outcomes.
//
// So every call below is wrapped: a success:false body is turned into a thrown
// error carrying the server's own sentence, which is what the components already
// catch and display. Contained here on purpose — apiClient is shared with
// jobService, and an interceptor there would change behaviour for the existing
// job and template screens.
const guard = (fn) =>
  async (...args) => {
    const res = await fn(...args);
    if (res?.data && res.data.success === false) {
      const error = new Error(res.data.statusMessage || "Request failed");
      error.statusMessage = res.data.statusMessage;
      error.handledByApi = true;
      throw error;
    }
    return res;
  };

const guardAll = (calls) =>
  Object.fromEntries(
    Object.entries(calls).map(([name, fn]) => [name, guard(fn)]),
  );

export const departmentService = guardAll({
  // ── Departments ────────────────────────────────────────────────────────
  createDepartment: (payload) => apiClient.post("/departments", payload),

  // `include=counts` returns level / template / open-job totals alongside each
  // department so the card grid renders from a single request.
  getDepartmentsByStore: (store_id, { includeCounts = true } = {}) =>
    apiClient.get(`/departments/store/${store_id}`, {
      params: includeCounts ? { include: "counts" } : undefined,
    }),

  getDepartmentById: (department_id) =>
    apiClient.get(`/departments/${department_id}`),

  updateDepartment: (department_id, payload) =>
    apiClient.put(`/departments/${department_id}`, payload),

  deleteDepartment: (department_id) =>
    apiClient.delete(`/departments/${department_id}`),

  // The whole tree in one call: nested levels, template stubs, member counts.
  getDepartmentTree: (department_id) =>
    apiClient.get(`/departments/${department_id}/tree`),

  // Every department, its positions and its people in one read. Backs the org
  // structure screen; `include: 'all'` keeps departments that have no positions.
  getOrgStructure: ({ includeEmpty = false } = {}) =>
    apiClient.get('/org-structure', {
      params: includeEmpty ? { include: 'all' } : undefined,
    }),

  // ── Levels (hierarchy nodes) ───────────────────────────────────────────
  // The server derives level_no and path from parent_id; the client never sends
  // either, so a stale tree in one tab cannot write a wrong depth.
  createNode: (payload) => apiClient.post("/dept-nodes", payload),

  updateNode: (node_id, payload) =>
    apiClient.put(`/dept-nodes/${node_id}`, payload),

  moveNode: (node_id, payload) =>
    apiClient.patch(`/dept-nodes/${node_id}/move`, payload),

  deleteNode: (node_id, { cascade = false } = {}) =>
    apiClient.delete(`/dept-nodes/${node_id}`, { params: { cascade } }),

  getNodeTemplates: (node_id) =>
    apiClient.get(`/dept-nodes/${node_id}/templates`),

  getNodeJobs: (node_id) => apiClient.get(`/dept-nodes/${node_id}/jobs`),

  // Attach an existing library template to a level, or detach it by passing
  // nulls. The server trusts the level over any department sent alongside it, so
  // the two columns can never disagree.
  assignTemplate: (template_id, { department_id = null, dept_node_id = null } = {}) =>
    apiClient.put(`/job-templates/${template_id}/assign`, {
      department_id,
      dept_node_id,
    }),

  // Templates with no level yet — what the "attach existing" picker offers.
  // Filtered client-side because the store listing is already one request and
  // the counts are small.
  getUnassignedTemplates: (store_id) =>
    apiClient.get(`/job-templates/store/${store_id}`),

  // ── Members ────────────────────────────────────────────────────────────
  // A membership row carries department_id always and dept_node_id optionally;
  // a null node means "somewhere in this department, no particular level",
  // which is how a recruiter covering all of Engineering is expressed.
  addMember: (payload) => apiClient.post("/dept-members", payload),

  addMembersBulk: (payload) => apiClient.post("/dept-members/bulk", payload),

  // include_inherited pulls in memberships held on ancestor levels — a lead on
  // Frontend covers everything under it, resolved by path prefix at read time
  // rather than by duplicating rows onto every descendant.
  getNodeMembers: (node_id, { includeInherited = false } = {}) =>
    apiClient.get(`/dept-members/node/${node_id}`, {
      params: includeInherited ? { include_inherited: true } : undefined,
    }),

  getDepartmentMembers: (department_id) =>
    apiClient.get(`/dept-members/department/${department_id}`),

  // Every department and level one person sits on. This is the multi-department
  // question answered from the person's side.
  getUserPlacements: (user_id) =>
    apiClient.get(`/dept-members/user/${user_id}`),

  updateMember: (member_id, payload) =>
    apiClient.put(`/dept-members/${member_id}`, payload),

  removeMember: (member_id) => apiClient.delete(`/dept-members/${member_id}`),
});

// The hiring department endpoints are new, so a front end deployed ahead of the
// API gets 404s from every one of them. That reads as "this feature is broken"
// unless it is told apart from a genuine failure, so callers use this to show
// "not deployed yet" instead of a red error.
export const isEndpointMissing = (err) => err?.response?.status === 404;

export const readApiError = (err, fallback) =>
  // `statusMessage` first: that is what guard() attaches when the API reported a
  // failure inside a 200, and it carries the server's own wording.
  err?.statusMessage ||
  err?.response?.data?.statusMessage ||
  err?.response?.data?.message ||
  fallback;
